import { FC, useState } from "react";

import { Stack, Text } from "@bps/fluent-ui";
import { Form, FormMethods } from "@bps/form-api";

import { ErrorResult } from "../../api/base";
import { FormStatusEnum } from "../../hooks/useFormApi.types";
import { pinFields } from "./VerifyPinForm.constants";
import { VerifyPinFormValues } from "./VerifyPinForm.interface";
import { VerifyPinFormError } from "./VerifyPinFormError";
import { VerifyPinFormFields } from "./VerifyPinFormFields";
import { VerifyPinFormHeader } from "./VerifyPinFormHeader";
import { VerifyPinFormTooManyAttempts } from "./VerifyPinFormTooManyAttempts";

type VerifyPinFormProps = {
  status: FormStatusEnum | undefined;
  submitPin: (pin: string) => Promise<void>;
};

export const VERIFY_PIN_FORM_MAX_WIDTH = 340;

// This component is a simplified version of the component from Bps.Platform.BpId.WebApp

export const VerifyPinForm: FC<VerifyPinFormProps> = ({
  status,
  submitPin
}) => {
  const [attempts, setAttempts] = useState(0);
  const [currentError, setError] = useState<ErrorResult | undefined>(
    status === FormStatusEnum.TooManyAttempts ? { status } : undefined
  );

  const initialValues: VerifyPinFormValues = {
    a: "",
    b: "",
    c: "",
    d: "",
    e: "",
    f: ""
  };

  const validate = (values: VerifyPinFormValues) => {
    const result: Record<string, string> = {};
    pinFields.forEach(key => {
      if (!values[key]) {
        result[key] = "Required";
      }
    });

    return result;
  };

  const onSubmit = (
    { a, b, c, d, e, f }: VerifyPinFormValues,
    form: Pick<FormMethods<VerifyPinFormValues>, "setValue" | "reset">
  ) => {
    return submitPin([a, b, c, d, e, f].join("")).catch(error => {
      setError(error);
      setAttempts(value => value + 1);

      form.reset();
    });
  };

  return (
    <Stack horizontalAlign="center" verticalAlign="center" verticalFill grow>
      <Stack
        tokens={{ childrenGap: 24 }}
        styles={{
          root: {
            marginBottom: 20, // to make the panel look more centred
            maxWidth: VERIFY_PIN_FORM_MAX_WIDTH,
            textAlign: "center"
          }
        }}
      >
        {currentError?.status === FormStatusEnum.TooManyAttempts ? (
          <VerifyPinFormTooManyAttempts error={currentError} />
        ) : (
          <>
            <Stack tokens={{ childrenGap: 8 }}>
              <VerifyPinFormHeader />
              <Text>
                Enter your date of birth to confirm your identity and ensure
                this message is intended for you
              </Text>
              <VerifyPinFormError error={currentError} attempts={attempts} />
            </Stack>
            <Form.Provider<VerifyPinFormValues>
              initialValues={initialValues}
              validate={validate}
              onSubmit={onSubmit}
            >
              {() => <VerifyPinFormFields error={currentError} />}
            </Form.Provider>
          </>
        )}
      </Stack>
    </Stack>
  );
};
